import './VerificationLanding.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as IcStars } from './assets/stats.svg';

import { Block, Button, Header, ScrollView } from '$uikit';
import { IcLogo40, IcPlay28 } from '$assets';
import { PROMO_MODELS } from '$shared/constants';

import { AUTH_REQUIRED, VERIFICATION_APPLY } from '$shared/constants/pages';
import { Loader, MediaViewer, Slider, Truncate } from '$shared/components';
import { goTo } from '$navigation/helper';
import { useDispatch, useSelector } from 'react-redux';
import { monetizationActions, monetizationSelector } from '$store/monetization';
import { authIsLoggedSelector, authSelector } from '$store/auth';
import { setStorageItem } from '$utils';
import { ContentPriorityType } from '$store/main/interface';
import { useCurrentLang, useTranslator } from '$hooks';
import { AttachItem, AttachType } from '$store/models';

const formatName = (string) => {
  const [firstName, lastName] = string.split(' ');
  return `${firstName} ${lastName ? lastName[0] + '.' : ''}`;
};

export const VerificationLanding: FC = () => {
  const isLogged = useSelector(authIsLoggedSelector);
  const { isLoading, status } = useSelector(monetizationSelector);
  const dispatch = useDispatch();
  const [viewPhotoIndex, setViewPhotoIndex] = useState<number | null>(null);
  const t = useTranslator();
  const lang = useCurrentLang();

  useEffect(() => {
    setStorageItem('contentPriorityType', ContentPriorityType.ADULT);
  }, []);

  useEffect(() => {
    if (isLogged) {
      dispatch(monetizationActions.loadMonetization());
    }
  }, [isLogged]);

  useEffect(() => {
    if (isLogged && !isLoading && status !== 'not_verified') {
      goTo(VERIFICATION_APPLY, {}, { replace: true });
    }
  }, [isLogged, isLoading, status]);

  const handleApply = useCallback(() => {
    if (isLogged) {
      goTo(VERIFICATION_APPLY);
    } else {
      goTo(AUTH_REQUIRED, {
        target: '/verification/apply',
      });
    }
  }, [isLogged]);

  const attachments = useMemo(() => {
    return PROMO_MODELS.map((item, index) => {
      if (item.video) {
        return {
          type: AttachType.VIDEO,
          video: {
            videoId: index,
            photoLarge: item.photoLarge,
            source: JSON.stringify([{ path: item.video }]),
            width: item.width,
            height: item.height,
          },
        };
      } else {
        return {
          type: AttachType.PHOTO,
          photo: {
            photoId: index,
            photoLarge: item.photoLarge,
            photoMedium: item.photoLarge,
            width: item.width,
            height: item.height,
          },
        };
      }
    }) as AttachItem[];
  }, [PROMO_MODELS]);

  if (isLoading && isLogged) {
    return <Loader isWrapped />;
  }

  return (
    <>
      <Header>
        <div className="VerificationLanding__header">
          <IcLogo40 />
          Ton.Place
        </div>
      </Header>
      <ScrollView
        footer={
          <Button flare onClick={handleApply}>
            {t('verification_button_apply')}
          </Button>
        }
      >
        <div className="VerificationLanding__title">
          <Truncate text={t('verification_title')} />
        </div>
        <div className="VerificationLanding__subTitle">{t('verification_subtitle')}</div>
        {lang.key === 'ru' && (
          <Block>
            <div className="VerificationLanding__video">
              <iframe
                src="https://www.youtube.com/embed/JB_CCerx1ec?si=YH6phhzGoAR648Od"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </div>
          </Block>
        )}
        <Block className="VerificationLanding__modelBlock">
          <div className="VerificationLanding__modelBlock__title">
            {t('verification_models_title')}
          </div>
          <div className="VerificationLanding__modelBlock__desc">
            {t('verification_models_desc')}
          </div>
          {viewPhotoIndex !== null && ( // Hack for offline (unsigned) users
            <MediaViewer
              decryptKey={''}
              active={true}
              startFrom={viewPhotoIndex}
              attachments={attachments}
              onClose={() => setViewPhotoIndex(null)}
            />
          )}
          <Slider className="VerificationLanding__models">
            {PROMO_MODELS.map((item, index) => (
              <div key={item.avatar} className="VerificationLanding__model">
                <div
                  className="VerificationLanding__model__image"
                  onClick={() => setViewPhotoIndex(index)}
                  style={{ backgroundImage: `url(${item.photoLarge})` }}
                >
                  {!!item.video && (
                    <div className="VerificationLanding__model__image__play">
                      <IcPlay28 />
                    </div>
                  )}
                  {/*<div className="VerificationLanding__model__image__name">*/}
                  {/*  {item.name}*/}
                  {/*</div>*/}
                </div>
                <a
                  href={'https://ton.place/' + (item.domain || `id${item.id}`)}
                  className="VerificationLanding__model__platform"
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="VerificationLanding__model__icon" />
                  {formatName(item.name)}
                </a>
                <a
                  href={'https://instagram.com/' + item.instagram}
                  className="VerificationLanding__model__instagram"
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="VerificationLanding__model__icon" />
                  {item.instagramCount}
                </a>
              </div>
            ))}
          </Slider>
        </Block>
        <Block className="VerificationLanding__textBlock">
          <div className="VerificationLanding__textBlock__stars purple">
            <IcStars />
          </div>
          <h2>{t('verification_block1_title_1')}</h2>
          <p>
            <Truncate text={t('verification_block1_desc2')} />
          </p>
          <h3>
            <Truncate text={t('verification_block1_title2')} />
          </h3>
          <p>
            <Truncate text={t('verification_block1_desc1')} />
          </p>

          <h3>{t('verification_block1_title3')}</h3>
          <p>
            <Truncate text={t('verification_block1_desc3')} />
          </p>
        </Block>
        <Block className="VerificationLanding__textBlock">
          <div className="VerificationLanding__textBlock__stars green">
            <IcStars />
          </div>
          <h2>{t('verification_block2_title1')}</h2>
          <p>
            <Truncate text={t('verification_block2_desc1')} />
          </p>

          <h3>{t('verification_block2_title2')}</h3>
          <p>
            <Truncate text={t('verification_block2_desc2')} />
          </p>

          <h3>{t('verification_block2_title3')}</h3>
          <p>
            <Truncate findLinks={false} text={t('verification_block2_desc3')} />
          </p>
        </Block>

        <Block className="VerificationLanding__textBlock">
          <div className="VerificationLanding__textBlock__stars blue">
            <IcStars />
          </div>
          <h2>{t('verification_block3_title1')}</h2>
          <p>
            <Truncate findLinks={false} text={t('verification_block3_desc1')} />
          </p>
        </Block>

        <div className="VerificationLanding__footer">
          <div className="VerificationLanding__footer__title">
            {t('verification_footer_title')}
          </div>
          <div className="VerificationLanding__footer__desc">
            <Truncate text={t('verification_footer_desc')} />
          </div>
        </div>
      </ScrollView>
    </>
  );
};
